
import React, { useState, useEffect } from "react";
import { Drawer, Button } from "antd";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import { addEvent, updateEventInfo } from "../../redux/actions/event";
import {
  selectUpdateEventLoading,
  selectUpdateEventSuccess,
  selectUpdateEventError,
  selectAddEventLoading,
  selectAddEventSuccess,
  selectAddEventError
} from "../../redux/selectors/eventSelectors";

// Import components
import MainDetailsSection from './components/MainDetailsSection';
import TicketDetailsSection from './components/TicketDetailsSection';
import AccessSlotsSection from './components/AccessSlotsSection';
import PaymentOptionsSection from './components/PaymentOptionsSection';
import EventViewMode from './components/EventViewMode';

// Import hooks
import { useEventFormValidation } from './hooks/useEventFormValidation';
import { useEventFormHandlers } from './hooks/useEventFormHandlers';
import { listEventVendors } from "../../redux/actions/eventVendor";
import { formatDate } from './../../helpers/date';

const SwEventDrawer = ({ onClose, open, mode = "add", eventData = null }) => {
  const dispatch = useDispatch();
  const [hasShownError, setHasShownError] = useState({ create: false, update: false });
  
  // Redux state selectors
  const createLoading = useSelector(selectAddEventLoading);
  const updateLoading = useSelector(selectUpdateEventLoading);
  const createSuccess = useSelector(selectAddEventSuccess);
  const createError = useSelector(selectAddEventError);
  const updateSuccess = useSelector(selectUpdateEventSuccess);
  const updateError = useSelector(selectUpdateEventError);

  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";
  const isLoading = isEditMode ? updateLoading : createLoading;

  // Get validation schema
  const { validationSchema } = useEventFormValidation();

  const formik = useFormik({
    initialValues: {
      event_name: '',
      location: '',
      start_date: '',
      end_date: '',
      ticketTypes: [
        { accessType: 'Regular', price: '', description: '' },

      ],
      accessSlots: {
        Regular: '',
      },
      paymentOption: 'card'
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setHasShownError({ create: false, update: false });
        
        const eventPayload = {
          event_name: values.event_name.trim(),
          location: values.location.trim(),
          start_date: formatDateForAPI(values.start_date),
          end_date: formatDateForAPI(values.end_date),
          payment_method: values.paymentOption === 'bank_transfer' ? 'bank_transfer' : 'card',
          access_types: values.ticketTypes
            .filter(ticket => ticket.accessType && ticket.accessType.trim() && ticket.price)
            .map(ticket => ({
              name: ticket.accessType.trim(),
              price: parseInt(ticket.price) || 0,
              slots: parseInt(values.accessSlots[ticket.accessType]) || 0,
              description: ticket.description ? ticket.description.trim() : ''
            }))
        };

        if (isEditMode && eventData?.id) {
          eventPayload.id = eventData.id;
          dispatch(updateEventInfo(eventPayload));
        } else {
          dispatch(addEvent(eventPayload));
        }
      } catch (error) {
        message.error("An unexpected error occurred. Please try again.");
      }
    }
  });

  // Get form handlers
  const {
    handleChange,
    handleBlur,
    handleTicketTypeChange,
    handleAccessSlotChange,
    handlePaymentOptionChange,
    addNewTicketType,
    removeTicketType
  } = useEventFormHandlers(formik);

  // Handle success states
  useEffect(() => {
    if (createSuccess && !isEditMode) {
      message.success("Event created successfully!");
      dispatch(listEventVendors());
      setTimeout(() => {
        handleClose();
      }, 1000);
    }
  }, [createSuccess, isEditMode]);

  useEffect(() => {
    if (updateSuccess && isEditMode) {
      message.success("Event updated successfully!");
      dispatch(listEventVendors());
      setTimeout(() => {
        handleClose();
      }, 1000);
    }
  }, [updateSuccess, isEditMode]);

  // Handle error states
  useEffect(() => {
    if (createError && !isEditMode && !hasShownError.create) {
      const errorMessage = typeof createError === 'string' ? createError : createError?.message || "Failed to create event";
      message.error(errorMessage);
      setHasShownError(prev => ({ ...prev, create: true }));
    }
  }, [createError, isEditMode, hasShownError.create]);

  useEffect(() => {
    if (updateError && isEditMode && !hasShownError.update) {
      const errorMessage = typeof updateError === 'string' ? updateError : updateError?.message || "Failed to update event";
      message.error(errorMessage);
      setHasShownError(prev => ({ ...prev, update: true }));
    }
  }, [updateError, isEditMode, hasShownError.update]);

  // Reset states when modal opens or mode changes
  useEffect(() => {
    if (open) {
      setHasShownError({ create: false, update: false });
      message.destroy();
    }
  }, [open, mode]);

  // Populate form when in edit mode
  useEffect(() => {
    if ((isEditMode || isViewMode) && eventData) {
      const ticketTypes = eventData.access_types ? 
        eventData.access_types.map(accessType => ({
          accessType: accessType.name,
          price: accessType.price.toString(),
          description: accessType.description || ''
        })) : [
          { accessType: 'Regular', price: '', description: '' },
          { accessType: 'VIP', price: '', description: '' },
          { accessType: 'Table', price: '', description: '' }
        ];
      
      const accessSlots = eventData.access_types ? 
        eventData.access_types.reduce((acc, accessType) => {
          acc[accessType.name] = accessType.available_quantity.toString();
          return acc;
        }, {}) : {};
        
      
      formik.setValues({
        event_name: eventData.name || '',
        location: eventData.location || '',
        start_date: formatDateForInput(eventData.start_date) || '',
        end_date: formatDateForInput(eventData.end_date) || '',
        guest_counts: eventData.guest_counts?? 0,
        ticketTypes: ticketTypes,
        accessSlots: accessSlots,
        paymentOption: eventData.payment_method?? 'card'
      });
    } else if (!isViewMode) {
      formik.resetForm();
    }
  }, [isEditMode, isViewMode, eventData, open]);

  const handleClose = () => {
    formik.resetForm();
    setHasShownError({ create: false, update: false });
    message.destroy();
    onClose();
  };

  const handleSaveChanges = () => {
    formik.submitForm();
  };

  // Helper function to get field error
  const getFieldError = (fieldName) => {
    const fieldPath = fieldName.split('.');
    let touched = formik.touched;
    let errors = formik.errors;
    
    for (const path of fieldPath) {
      if (touched && typeof touched === 'object') {
        touched = touched[path];
      }
      if (errors && typeof errors === 'object') {
        errors = errors[path];
      }
    }
    
    return touched && errors ? errors : null;
  };

  // Helper function to get ticket type error
  const getTicketTypeError = (index, field) => {
    return formik.touched.ticketTypes?.[index]?.[field] && 
           formik.errors.ticketTypes?.[index]?.[field] 
           ? formik.errors.ticketTypes[index][field] 
           : null;
  };

  // Dynamic drawer title based on mode
  const drawerTitle = isEditMode ? "Edit Event Details" : isViewMode ? "Event Details" : "Add Event Details";

  // Helper function to format price
  const formatPrice = (price) => {
    if (!price) return '';
    return `₦ ${price.toLocaleString()}`;
  };

  // Helper function to get payment option display text
  const getPaymentOptionText = (option) => {
    switch (option) {
      case 'bank_transfer':
        return 'Bank Transfer';
      case 'card':
        return 'Card';
      default:
        return option;
    }
  };

  // Calculate total attendees for view mode
  const getTotalAttendees = () => {
    return formik.values.guest_counts?? 0;
  };

  // Format date for display (convert from API format DD-MM-YYYY to display format)
  const formatDateForDisplay = (dateString) => {
    return formatDate(dateString?? '');
  };

  // Format date for API (convert from input YYYY-MM-DD to DD-MM-YYYY format)
  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  };

  // Format date for input (convert from API DD-MM-YYYY to input YYYY-MM-DD format)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length === 3 && parts[0].length <= 2) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  // Get active access types (those with non-empty accessType values)
  const getActiveAccessTypes = () => {
    return formik.values.ticketTypes
      .filter(ticket => ticket.accessType && ticket.accessType.trim())
      .map(ticket => ticket.accessType);
  };

  // Render view mode
  if (isViewMode) {
    return (
      <EventViewMode
        open={open}
        handleClose={handleClose}
        drawerTitle={drawerTitle}
        formik={formik}
        eventData={eventData}
        formatDateForDisplay={formatDateForDisplay}
        formatPrice={formatPrice}
        getPaymentOptionText={getPaymentOptionText}
        getTotalAttendees={getTotalAttendees}
      />
    );
  }

  // Render edit/add mode
  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      onClose={handleClose}
      open={open}
      width={600}
      closeIcon={
        <svg
          width="17"
          height="15"
          viewBox="0 0 17 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17 7.27429C17 7.65399 16.7178 7.96779 16.3518 8.01745L16.25 8.02429L3.066 8.02369L7.82899 12.7672C8.12251 13.0595 8.12353 13.5344 7.83127 13.8279C7.56558 14.0947 7.14897 14.1198 6.85489 13.9026L6.77061 13.8302L0.720613 7.80617C0.681923 7.76764 0.648317 7.72595 0.619795 7.68192C0.611738 7.66866 0.603539 7.65511 0.595764 7.64127C0.588614 7.62942 0.582151 7.61711 0.576056 7.60466C0.567589 7.58655 0.559317 7.56784 0.551805 7.54875C0.545702 7.53403 0.540607 7.51972 0.535959 7.50529C0.530434 7.48739 0.525064 7.46832 0.520447 7.44897C0.517014 7.43545 0.514294 7.42244 0.511923 7.40938C0.508587 7.38995 0.505749 7.36984 0.503723 7.34949C0.501974 7.33398 0.500921 7.3186 0.50034 7.30321C0.500189 7.29388 0.5 7.28411 0.5 7.27429L0.500376 7.24524C0.50095 7.23052 0.501955 7.21582 0.503393 7.20114L0.5 7.27429C0.5 7.22697 0.504383 7.18066 0.512768 7.13576C0.514711 7.12504 0.517029 7.11403 0.519594 7.10307C0.524921 7.08049 0.531078 7.05875 0.538168 7.03744C0.541647 7.02685 0.545702 7.01553 0.550034 7.0043C0.558798 6.98173 0.568325 6.96022 0.578804 6.93927C0.583673 6.92941 0.589125 6.9191 0.594835 6.90889C0.604208 6.89223 0.613863 6.8764 0.624073 6.86097C0.631277 6.85005 0.639254 6.83868 0.647583 6.82749L0.654074 6.81885C0.67428 6.79245 0.696201 6.76743 0.719669 6.74396L0.72057 6.74327L6.77057 0.718268C7.06407 0.425982 7.53894 0.426965 7.83123 0.720464C8.09694 0.987281 8.12029 1.40399 7.90182 1.69715L7.82903 1.78112L3.068 6.52369L16.25 6.52429C16.6642 6.52429 17 6.86008 17 7.27429Z"
            fill="#2C264B"
          />
        </svg>
      }
    >
      <div className="sw-event-drawer-content">
        <form onSubmit={formik.handleSubmit}>
          <MainDetailsSection
            formik={formik}
            getFieldError={getFieldError}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />

          <TicketDetailsSection
            formik={formik}
            handleTicketTypeChange={handleTicketTypeChange}
            getTicketTypeError={getTicketTypeError}
            addNewTicketType={addNewTicketType}
            removeTicketType={removeTicketType}
          />

          <AccessSlotsSection
            formik={formik}
            getActiveAccessTypes={getActiveAccessTypes}
            handleAccessSlotChange={handleAccessSlotChange}
            getFieldError={getFieldError}
          />

          <PaymentOptionsSection
            formik={formik}
            handlePaymentOptionChange={handlePaymentOptionChange}
          />
        </form>
        
        {/* Action Buttons */}
        <div className="sw-action-buttons">
          <Button
            onClick={handleClose}
            className="sw-cancel-btn"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSaveChanges}
            loading={isLoading}
            className="sw-save-btn"
          >
            {isEditMode ? "Update Event" : "Create Event"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default SwEventDrawer;